import { API_PERMISSIONS, MarketNetwork } from "../cefi";
import { InternalChainId, InternalChainIdSchema } from "../chains";
import { DecimalSchema } from "../decimal";
import {
  WalletType,
  TokenSymbol,
  PairSymbol,
  OrderSide,
  OrderType,
  MarginMode,
  TxType,
  TxStatus,
  Fee,
  Timeframe,
  PairSymbolSchema,
  TokenSymbolSchema,
  FeeSchema,
  FeeV2Schema,
  ORDER_SIDE,
  ORDER_TYPE,
  MARGIN_MODE,
  ORDER_STATUS,
  ExchangeStatus,
  POSITION_SIDE,
} from "../shared";
import { AccountId } from "../vault";
// V2 Types with Decimal.js precision
import type Decimal from "decimal.js";
import z from "zod";

export type VaultId = string;

export type Pong = "pong";

export type Transaction<
  F extends CefiInfoTx | DefiInfoTx = CefiInfoTx | DefiInfoTx,
  T extends CefiInfoTx | DefiInfoTx = CefiInfoTx | DefiInfoTx,
> = {
  from: F;
  to: T;
  type: TxType;
  pairSymbol?: string; // Only for trades
  partnerId?: number;
};

export type TemporaryTransaction = Transaction & {
  isTemp: boolean;
};

export type StoreTransaction = Transaction & {
  id: string; // Unique identifier for the transaction whatever the type is or the exchange is
  vaultId: VaultId;
  timestamp: number;
};

// TxStatus and TxType are now imported from ../shared

export type PrepareWithdrawalParams = {
  accountId: AccountId;
  tokenSymbol: TokenSymbol;
  amount: number;
  address?: string;
  network?: InternalChainId;
  opts?: {
    tradeAndSend?: boolean;
    isInternalTransfer?: boolean;
  };
};

export type VerifyOrderParams = {
  orderId: string;
  turnstileToken: string;
};

export type TotupWalletTypeParams = {
  accountId: AccountId;
  amount: number;
  tokenSymbol: TokenSymbol;
  action: AssetMovementAction;
};

// WalletType, WalletTypeV1, and isWalletType are now imported from ../shared

export const AUTHENTICATION_METHOD = {
  OAUTH: "oauth",
  API_KEYS: "apiKeys",
} as const;
export type AUTHENTICATION_METHOD = (typeof AUTHENTICATION_METHOD)[keyof typeof AUTHENTICATION_METHOD];

export const ExchangeInfoSchema = z.object({
  name: z.string(), // The exchange label
  id: z.string(), // The exchange identifier
  logo: z.string(), // The exchange logo
  status: z.enum(ExchangeStatus), // The exchange API's status (e.g. "ok", "shutdown", "error", "maintenance", "unknown")
  supportedWalletTypes: z.array(z.string()), // The exchange's internal wallet types supported (e.g. ["spot", "margin", "futures"])
  supportedFeatures: z.array(z.string()), // The exchange's supported features (e.g. ["send", "trade", "trade_and_send", "receive"])
  authenticationMethod: z.enum(AUTHENTICATION_METHOD), // The exchange's authentication method
  isRequiringEmailConfirmation: z.boolean(), // True if the exchange requires email confirmation
  minimumWithdrawalUrl: z.string().optional(),
  minimumDepositUrl: z.string().optional(),
  isRequiringAddressWhitelisting: z.boolean(), // True if the exchange requires address whitelisting for withdrawals
  provideWhitelistedAddresses: z.boolean(), // True if the exchange provides whitelisted addresses
  ipWhitelistAddresses: z.array(z.string()), // List of IP addresses that should be whitelisted for a set of credentials
  whitelistScopes: z.array(z.enum(API_PERMISSIONS)).optional(), // List of scopes of API permissions that require the use of whitelisted ip addresses
  featuresUnderMaintenance: z.object({
    // The set of features that are under maintenance
    send: z.boolean(),
    trade: z.boolean(),
    receive: z.boolean(),
    trade_and_send: z.boolean(),
    limit_trade: z.boolean(),
  }),
  apiManagementLink: z.string().optional(), // Link to the exchange's API management page
  addressManagementUrl: z.string().optional(), // Link to the exchange's address whitelisting and management page
  affiliationLink: z.string().optional(), // Cede Labs affiliation link
  hybridAuthentication: z
    .object({
      // For hybrid exchanges accepting both OAuth and API keys, the permissions allowed for each authentication method
      oauth: z.array(z.enum(API_PERMISSIONS)),
      api_keys: z.array(z.enum(API_PERMISSIONS)),
    })
    .optional(),
  oauthAuthentication: z.object({
    // Exchange OAuth authentication information
    isOauthLoginSupported: z.boolean(),
    hasPkceFlow: z.boolean(),
    isUsingFastApi: z.boolean(),
  }),
  requiresUid: z.boolean(), // True if the exchange requires an UID for authentication
  requiresPassword: z.boolean(), // True if the exchange requires a password for authentication
  requiresTwoKeys: z.boolean(), // True if the exchange requires two different keys, one for reading/trading and one for withdrawals
  inducedPermissions: z.partialRecord(z.enum(API_PERMISSIONS), z.array(z.enum(API_PERMISSIONS))).optional(), // Set of permissions that are induced by other permissions
  sendWalletType: z.enum(WalletType).optional(), // The wallet type that is used for withdrawals
  transferWalletTypes: z.array(z.enum(WalletType)).optional(), // List of internal wallet types that support transfers between them
  notConnectedIdentifier: z.string().optional(),
  websiteHomelinkIdentifier: z.string().nullable(),
  supportVerifyKYC: z.boolean(), // True if the exchange supports KYC verification
});

export type ExchangeInfo = z.infer<typeof ExchangeInfoSchema>;

export type ExchangeInfoById = { [key: string]: ExchangeInfo };

// Fee and FeeWithAmounts are now imported from ../shared

export type CefiTx = {
  amount: number;
  refAmount?: number;
  tokenSymbol: TokenSymbol;
  transactionId: string;
  fee: Fee;
  status: TxStatus;
  timestamp: number;
};

export type CefiInfoTx = {
  account: {
    exchangeId: string;
    accountId: AccountId;
    network?: string;
  };
  wallet: {
    depositAddress: string;
    walletType: WalletType;
  };
  transaction: CefiTx;
};

export function isCefiInfoTx(tx: CefiInfoTx | DefiInfoTx): tx is CefiInfoTx {
  return typeof tx === "object" && tx !== null && Object.prototype.hasOwnProperty.call(tx, "wallet");
}

export type DefiInfoTx = {
  account: {
    address: string;
    network: InternalChainId;
  };
  transaction: {
    transactionHash: string;
    fee: Fee;
    status: TxStatus;
    timestamp: number;
    tokens: {
      amount: number;
      refAmount?: number;
      tokenSymbol: TokenSymbol;
      address: string; //  why do we need an address here? I think we should remove it
    }[];
  };
};

export function isDefiInfoTx(tx: CefiInfoTx | DefiInfoTx): tx is DefiInfoTx {
  return !isCefiInfoTx(tx);
}

// TODO merge with TokenBalance type
export type TokenBalanceTypes = {
  tokenName?: string;
  freeBalance: number;
  refFreeBalance: number;
  usedBalance: number;
  refUsedBalance: number;
  totalBalance: number;
  refTotalBalance: number;
  isAllowedAsCollateral?: boolean;
  crossMarginBalanceUsd?: {
    free: number;
    used: number;
    total: number;
  };
  marginBalance?: {
    [symbol: string]: {
      freeBalance: number;
      totalBalance: number;
      type: "base" | "quote";
    };
  };
};

/**
 * Updated version of TokenBalanceTypes
 */
export type PortfolioBalanceEntry = {
  tokenName?: string;
  freeBalance: string;
  refFreeBalance: string;
  usedBalance: string;
  refUsedBalance: string;
  totalBalance: string;
  refTotalBalance: string;
  isAllowedAsCollateral?: boolean;
  marginBalance?: {
    [symbol: string]: {
      freeBalance: string;
      totalBalance: string;
      type: "base" | "quote";
    };
  };
};

export type GroupedBalancesByWalletType = Record<
  string,
  TokenBalanceTypes & Partial<Record<WalletType, TokenBalanceTypes>>
>;

export type BalancesByAccountByWalletType = {
  [accountId: string]: BalancesByWalletType;
};

export type BalancesByWalletType = {
  [walletType: string]: {
    [tokenSymbol: string]: TokenBalanceTypes;
  };
};

export type BalancesByWalletTypeV2 = {
  [walletType: string]: {
    [tokenSymbol: string]: PortfolioBalanceEntry;
  };
};

export type SubAccountBalancesByWalletType = BalancesByWalletType;
export type SubAccountBalancesByWalletTypeV2 = BalancesByWalletTypeV2;

export type BalancesByToken = {
  [tokenSymbol: string]: TokenBalanceTypes;
};

export type GroupedBalancesByAccount = {
  [accountId: string]: GroupedBalancesByWalletType;
};

export type BalanceDetailsByType = {
  [type: string]: TokenBalanceTypes;
};

/**
 * useful when the consumer needs to interact directly with the exchange,
 * without going through the Cede SDK
 */
const MinMaxSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
});

export const MarketPairSchema = z.object({
  externalMarketId: z.string(),
  pairSymbol: PairSymbolSchema,
  base: TokenSymbolSchema,
  quote: TokenSymbolSchema,
  type: z.nativeEnum(WalletType),
  taker: z.number(),
  maker: z.number(),
  active: z.boolean(),
  /**
   * Market category distinguishing FX pairs (e.g. EUR/USDT-P on Hibachi) from
   * regular crypto pairs. Hibachi sub-accounts are scoped to a single category
   * via `allowedTradableContractCategories`. Optional through-and-through;
   * callers that need to filter FX out should treat `undefined` as `"crypto"`.
   */
  category: z.enum(["crypto", "fx"]).optional(),
  precision: z.object({
    amount: z.number(),
    price: z.number(),
    base: z.number(),
    quote: z.number(),
  }),
  limits: z
    .object({
      leverage: MinMaxSchema.optional(),
      amount: MinMaxSchema.optional(),
      price: MinMaxSchema.optional(),
      cost: MinMaxSchema.optional(),
    })
    .optional(),
  maxLeverage: z.number().optional(),
});

export type MarketPair = z.infer<typeof MarketPairSchema>;

export const MarketPairV2Schema = MarketPairSchema.extend({
  markPrice: z.number().optional(),
  indexPrice: z.string().optional(),
  volume24h: z.string().optional(),
  priceChange24h: z.string().optional(),
  minOrderSize: z.number().optional(),
  tickSize: z.number().optional(),
  openInterest: z.number().optional(),
  marginMode: z.enum(MarginMode).optional(),
  settlementToken: z.string().optional(),
});

export type MarketPairV2 = z.infer<typeof MarketPairV2Schema>;

export type Ticker = {
  timestamp: number;
  open: number | undefined;
  high: number;
  close: number | undefined;
  low: number;
  last: number | undefined;
  average: number | undefined;
  baseVolume: number | undefined;
  quoteVolume: number | undefined;
};

export type Tickers = {
  [pairSymbol: PairSymbol]: Ticker;
};

export type Nft = {
  data?: {
    name: string;
    description: string;
    imageUrl: string;
    animationUrl?: string;
    attributes: Record<string, string | number>;
    collection: {
      name: string;
      imageUrl: string;
      creator: string;
    };
  };
  amount?: number;
  tokenId: string;
  tokenAddress: string;
  owner?: string;
  network: InternalChainId;
};

export type DepositableToken = {
  tokenSymbol: TokenSymbol;
  networks?: MarketNetwork[];
};

// Re-export token types from shared (moved to avoid circular dependency)
export type { TokenWithMarketNetwork, TokenWithMarketNetworkAndTokenName } from "../shared";

export type Network = {
  name: string;
  network: InternalChainId;
  chainId: number; // chain id in decimal format, if possible
};

export type ProviderConfig = {
  [key: string]: string;
};

export const DepositAddressSchema = z.object({
  tokenSymbol: TokenSymbolSchema,
  address: z.string(),
  network: InternalChainIdSchema,
  /**
   * @description
   * A tag or memo is a unique identifier assigned to each account
   * for identifying a deposit and crediting the appropriate account.
   *
   * Some exchanges require a tag or memo to be included with the deposit
   * for some tokens, such as BNB or ATOM
   */
  tag: z.string().optional(),
  /**
   * @description
   * A contract address for a token on a specific network
   */
  contract: z.string().optional(),
});
export type DepositAddress = z.infer<typeof DepositAddressSchema>;

// TokenSymbol and PairSymbol are now imported from ../shared

export const FuturePositionSchema = z.object({
  pairSymbol: PairSymbolSchema,
  marginTokenSymbol: TokenSymbolSchema,
  marginMode: z.enum(MarginMode),
  positionSide: z.enum(POSITION_SIDE),
  notional: z.string().optional(),
  timestamp: z.number().optional(),
  unrealizedPnl: z.string().optional(),
  leverage: z.string().optional(),
  entryPrice: z.string().optional(),
  percentage: z.string().optional(),
  initialMargin: z.string().optional(),
  initialMarginPercentage: z.string().optional(),
  size: z.number().optional(),
  liquidationPrice: z.string().optional(),
  isolationId: z.string().optional(),
});
export type FuturePosition = z.infer<typeof FuturePositionSchema>;

export const GetPositionsResponseSchema = z.record(z.string(), z.array(FuturePositionSchema));
export type GetPositionsResponse = z.infer<typeof GetPositionsResponseSchema>;

export const ApiFundingHistoryEntrySchema = z.object({
  id: z.string(),
  symbol: z.string(),
  code: z.string(),
  timestamp: z.number().optional(),
  datetime: z.string().optional(),
  amount: z.number(),
  fundingRate: z.number().optional(),
  positionSide: z.enum(POSITION_SIDE).optional(),
});
export type ApiFundingHistoryEntry = z.infer<typeof ApiFundingHistoryEntrySchema>;

export const ApiGetFundingHistoryResponseSchema = z.object({
  fundingHistory: z.array(ApiFundingHistoryEntrySchema),
  total: z.number(),
});
export type ApiGetFundingHistoryResponse = z.infer<typeof ApiGetFundingHistoryResponseSchema>;

export const EstimatedPriceFromOrderBookResponseSchema = z.object({
  estimatedPrice: z.number(),
});
export type EstimatedPriceFromOrderBookResponse = z.infer<typeof EstimatedPriceFromOrderBookResponseSchema>;

export const MarketRateSchema = z.object({
  bid: z.string(),
  ask: z.string(),
});
export type MarketRate = z.infer<typeof MarketRateSchema>;

export enum TakerOrMaker {
  MAKER = "maker",
  TAKER = "taker",
  UNKNOWN = "unknown",
}
export const TAKER_OR_MAKER = {
  MAKER: "maker",
  TAKER: "taker",
  UNKNOWN: "unknown",
} as const;
export type TAKER_OR_MAKER = (typeof TAKER_OR_MAKER)[keyof typeof TAKER_OR_MAKER];

export const TradeSchema = z.object({
  id: z.string(),
  timestamp: z.number(),
  pairSymbol: PairSymbolSchema,
  orderId: z.string().optional(),
  orderType: z.nativeEnum(ORDER_TYPE).optional(),
  side: z.nativeEnum(ORDER_SIDE),
  price: z.number(),
  amount: z.number(),
  takerOrMaker: z.nativeEnum(TAKER_OR_MAKER),
  fee: FeeSchema,
  closedPnl: z.string().optional(),
  /** Client-assigned order id, when the exchange echoes it on trade records. */
  clientOrderId: z.string().optional(),
  /** Raw exchange-specific fields, useful for downstream inspection. */
  info: z
    .object({
      /**
       * Aggressor + passive wallet addresses on a fill. Only populated by
       * Hyperliquid (REST `getMyTrades` and the public-WS feed). Used by the
       * PnL trade pipeline to attribute on-chain fills to hub users.
       */
      users: z.tuple([z.string(), z.string()]).optional(),
    })
    .catchall(z.unknown())
    .optional(),
});

export type Trade = z.infer<typeof TradeSchema>;

export const OrderSchema = z.object({
  id: z.string(),
  timestamp: z.number(),
  status: z.nativeEnum(ORDER_STATUS),
  pairSymbol: PairSymbolSchema,
  type: z.nativeEnum(ORDER_TYPE),
  side: z.nativeEnum(ORDER_SIDE),
  price: z.number(),
  amount: z.number(),
  filled: z.number(),
  fee: FeeSchema,
  clientOrderId: z.string().optional(),
  triggerPrice: z.number().optional(),
  isSelfTrade: z.boolean().optional(),
  reduceOnly: z.boolean().optional(),
  trades: z.array(TradeSchema).optional(),
  rejectionReason: z.string().optional(),
});

export type Order = z.infer<typeof OrderSchema>;

export const OpenOrdersSchema = z.record(z.string(), z.array(OrderSchema));
export type OpenOrders = z.infer<typeof OpenOrdersSchema>;

export const OrderBookSchema = z.object({
  asks: z.array(z.tuple([z.number(), z.number()])),
  bids: z.array(z.tuple([z.number(), z.number()])),
  timestamp: z.number(),
  /** Monotonic per-channel sequence for venues that expose it (e.g. Katana). Used by the FE to dedupe diffs. */
  sequence: z.number().optional(),
});
export type OrderBook = z.infer<typeof OrderBookSchema>;

export type OrderId = string;

export const TradeV2Schema = z.object({
  id: z.string(),
  timestamp: z.number(),
  pairSymbol: PairSymbolSchema,
  orderId: z.string().optional(),
  orderType: z.nativeEnum(ORDER_TYPE).optional(),
  side: z.nativeEnum(ORDER_SIDE),
  price: DecimalSchema,
  amount: DecimalSchema,
  takerOrMaker: z.nativeEnum(TAKER_OR_MAKER),
  fee: FeeV2Schema,
  custom: z.record(z.string(), z.any()).optional(),
});

export type TradeV2 = z.infer<typeof TradeV2Schema>;

export const OrderV2Schema = z.object({
  id: z.string(),
  timestamp: z.number(),
  status: z.nativeEnum(ORDER_STATUS),
  pairSymbol: PairSymbolSchema,
  type: z.nativeEnum(ORDER_TYPE),
  side: z.nativeEnum(ORDER_SIDE),
  price: DecimalSchema,
  amount: DecimalSchema,
  // ccxt omitZero() reports a 0 fill as undefined (e.g. bitmart canceled/rejected market orders)
  filled: DecimalSchema.optional(),
  fee: FeeV2Schema,
  clientOrderId: z.string().optional(),
  isSelfTrade: z.boolean().optional(),
  reduceOnly: z.boolean().optional(),
  triggerPrice: DecimalSchema.optional(),
  trades: z.array(TradeV2Schema).optional(),
  rejectionReason: z.string().optional(),
});

export type OrderV2 = z.infer<typeof OrderV2Schema>;

export type OrderBookV2 = {
  asks: [Decimal, Decimal][];
  bids: [Decimal, Decimal][];
  timestamp: number;
};

// Trading-related types are now imported from ../shared:
// ORDER_SIDE, OrderSide, PositionSide, MarginMode, OrderType, ORDER_TYPE,
// OrderStatus, ORDER_STATUS, NON_ELIGIBILITY_REASONS

export type Result = "success" | "error";

export type WithdrawToDefiResult = {
  id: string;
  timestamp: number;
};

export const EstimatedFeeSchema = FeeSchema;
export type EstimatedFee = z.infer<typeof EstimatedFeeSchema>;

export const EstimatedFeeAndAmountSchema = z.object({
  estimatedFee: EstimatedFeeSchema,
  estimatedAmount: z.string(), // in base currency
});
export type EstimatedFeeAndAmount = z.infer<typeof EstimatedFeeAndAmountSchema>;

export const CreateOrderRequestSchema = z.object({
  pairSymbol: PairSymbolSchema,
  orderSide: z.nativeEnum(ORDER_SIDE),
  orderType: z.nativeEnum(ORDER_TYPE),
  price: z.string(), // price from the ticker (base to quote).
  amount: z.string(), // the amount in base currency
  quoteAmount: z.string().optional(), // the original quote amount from user (for MEXC buy orders)
  leverage: z.number().optional(), // leverage for perpetual contracts (e.g., Hyperliquid)
  reduceOnly: z.boolean().optional(), // true to close/reduce position size (perpetual contracts)
  postOnly: z.boolean().optional(), // true to ensure the order is a maker order (rejected if it would match immediately)
  marginMode: z.nativeEnum(MARGIN_MODE).optional(), // margin mode for perpetual contracts
  takeProfitPrice: z
    .string()
    .regex(/^\d+(\.\d+)?([eE][+-]?\d+)?$/)
    .optional(),
  stopLossPrice: z
    .string()
    .regex(/^\d+(\.\d+)?([eE][+-]?\d+)?$/)
    .optional(),
  metadata: z
    .object({
      tradeAndSend: z.boolean().optional(),
      defiAddress: z.string().optional(),
    })
    .optional(),
});

export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const PreparedOrderSchema = EstimatedFeeAndAmountSchema.extend({
  createOrderRequest: CreateOrderRequestSchema,
});

export type PreparedOrder = z.infer<typeof PreparedOrderSchema>;

export type ConnectParams = {
  onboard?: boolean;
  readOnlyOnboarding?: boolean;
  silent?: boolean;
  callbackUrl?: string;
};

export const MinAmountsSchema = z.object({
  minBaseAmount: z.string(),
  minQuoteAmount: z.string(),
  minPrice: z.string(),
});

export type MinAmounts = z.infer<typeof MinAmountsSchema>;

export enum CancelOrderStatus {
  CANCELED = "canceled",
  ALREADY_FILLED = "already_filled",
}
export const CANCEL_ORDER_STATUS = {
  CANCELED: "canceled",
  ALREADY_FILLED: "already_filled",
} as const;
export type CANCEL_ORDER_STATUS = (typeof CANCEL_ORDER_STATUS)[keyof typeof CANCEL_ORDER_STATUS];

export const CancelOrderStatusResponseSchema = z.object({
  status: z.nativeEnum(CancelOrderStatus),
});
export type CancelOrderStatusResponse = z.infer<typeof CancelOrderStatusResponseSchema>;

export type WithdrawToDefiParams = {
  accountId: AccountId;
  tokenSymbol: TokenSymbol;
  network: string | number;
  amount: string;
  address: string;
  code?: string;
  withdrawalTag?: string;
  isInternalTransfer?: boolean;
  metadata?: {
    tradeAndSend?: boolean;
    referrerSource?: string;
    origin?: string;
    clientTxId?: string;
    callbackUrl?: string;
  };
};

export type GetWhitelistedAddressesParams = {
  accountId: AccountId;
  tokenSymbol?: TokenSymbol;
  network?: InternalChainId;
};
export enum AssetMovementAction {
  TRADE = "trade",
  SEND = "send",
}

export type TotupPath = {
  fromWalletType: WalletType;
  fromWalletTypeExternalName: string;
  toWalletType: WalletType;
  toWalletTypeExternalName: string;
};

export type WithdrawableTokensParams = {
  accountId: AccountId;
  network?: InternalChainId;
};

export type VaultAccountsParams = {
  vaultId: VaultId;
};
export type TransactionsParams = {
  vaultId: VaultId;
  accountIds?: AccountId[];
  tokenSymbol?: TokenSymbol;
  txType?: string;
  startTime?: number;
  pageCount?: number;
  pageIndex?: number;
};

export type BalancesParams = {
  vaultId: VaultId;
  accountId?: AccountId[];
  walletTypes?: WalletType[];
  opts?: {
    forceRefresh?: boolean;
  };
};

export type DepositableTokensParams = {
  accountId: AccountId;
};

export type GetNetworksParams = {
  accountId: AccountId;
  tokenSymbol?: TokenSymbol;
  opts: {
    toDeposit?: boolean;
    toWithdraw?: boolean;
    isInternalTransfer?: boolean;
  };
};

export type GetNetworksBetweenCexParams = {
  accountId: AccountId;
  toAccountId: AccountId;
  tokenSymbol: TokenSymbol;
};

export type GetSupportedTokensParams = {
  accountId: AccountId;
  network?: string;
};

export type DepositParams = {
  accountId: AccountId;
  tokenSymbol: TokenSymbol;
  network: InternalChainId;
};

export type OpenOrdersParams = {
  accountId: AccountId;
  pairSymbol: PairSymbol;
  since?: number;
  limit?: number;
};

export type CreateOrderParams = {
  accountId: AccountId;
  pairSymbol: PairSymbol;
  orderSide: OrderSide;
  orderType: OrderType;
  amount: string;
  price: string;
  metadata?: {
    tradeAndSend?: boolean;
    defiAddress?: string;
    exchangeUserId?: string;
  };
};

export type NftsParams = {
  accountId: AccountId;
};

export type RetrieveOrderParams = {
  accountId: AccountId;
  orderId: string;
  pairSymbol: PairSymbol;
  metadata?: {
    defiAddress?: string;
  };
};

export type EditOrderParams = {
  accountId: AccountId;
  orderId: OrderId;
  pairSymbol: PairSymbol;
  orderSide?: OrderSide;
  orderType?: OrderType;
  price?: string;
  amount?: string;
};

export type GetMinAmountsParams = {
  accountId: AccountId;
  pairSymbol: PairSymbol;
  orderSide: OrderSide;
  price: string;
};

export type MarketPairsParams = {
  accountId: AccountId;
};

export type MarketRateParams = {
  accountId: AccountId;
  pairSymbol: PairSymbol;
};

export type CancelOrderParams = {
  accountId: AccountId;
  orderId: OrderId;
  pairSymbol: PairSymbol;
};

export type AddCexParams = {
  exchangeId?: string;
  readOnlyOnboarding?: boolean;
  callbackUrl?: string;
};

export type GetWithdrawalByIdParams = {
  accountId: string;
  withdrawalId: string;
  tokenSymbol: string;
  timestamp: number;
};

/**
 * @param timestamp Only for withdrawal transaction
 * @param tokenOrPairSymbol For trade transaction, it's the pair symbol (ex: BTC/USDT) and for withdrawal transaction, it's the token symbol (ex: BTC)
 */
export type SearchTransactionParams = {
  accountId: string;
  transactionId: string;
  transactionType: "trade" | "withdrawal";
  tokenOrPairSymbol: string;
  timestamp?: number;
  metadata?: {
    defiAddress?: string;
  };
};

export type GetWidgetBundleUrlParams = {
  name: string;
};

export type WhitelistedAddresses = {
  address: string;
  key: string;
  network: InternalChainId;
  tokenSymbol?: TokenSymbol; // It's not defined for universal addresses
  verified: boolean;
};

export type GetTradePathParams = {
  accountId: string;
  to: string;
  from: string;
};

export type GetTradePathBySupportedTokensParams = {
  accountId: string;
  from: string;
  network: InternalChainId;
};

export type GetOHLCVParams = {
  accountId: string;
  pairSymbol: string;
  timeframe: Timeframe;
  from: number;
  to: number;
  limit: number;
  opts?: {
    forceRefresh?: boolean;
  };
};

export type VerifyKYCParams = {
  accountId: string;
  tokenSymbol?: TokenSymbol;
  network?: InternalChainId;
  callbackUrl: string;
};

export type VerifyKYCReturn = {
  balances: BalancesByWalletType;
  depositAddress: DepositAddress;
  exchangeId: string;
};

export enum UrlRoot {
  POPUP = "popup",
  REQUEST_ONBOARDING = "requestOnboarding",
  REQUEST = "request",
}

export type OpenTabParams = {
  route?: string;
  root?: UrlRoot;
};

/**
 * A STP group is a group of users for which the orders shouldn't match each other.
 * It's used to avoid self-trading.
 */
export type StpGroup = {
  stpGroupId: string;
  stpGroupName: string;
  createdAt: string;
  createdBy: string;
};

/**
 * A STP group member is a user who is part of a STP group.
 */
export type StpGroupMember = {
  stpGroupId: string;
  userId: string;
  createdAt: string;
};

// Re-export shared types for backward compatibility

// Export enums and const objects as values (needed for runtime usage)
export {
  WalletType,
  WalletTypeV1,
  ORDER_SIDE,
  ORDER_TYPE,
  ORDER_STATUS,
  NON_ELIGIBILITY_REASONS,
  OrderType,
  OrderStatus,
  MarginMode,
  TxStatus,
  isWalletType,
} from "../shared";

// Export type aliases as types only
export type {
  TokenSymbol,
  PairSymbol,
  OrderSide,
  PositionSide,
  TxType,
  Fee,
  FeeV2,
  FeeWithAmounts,
  MarginAssetMode,
} from "../shared";
